import type { MutableRefObject } from 'react';

import {
  libraryImageRenderer,
  type ResolvedLibraryImage,
} from '@/components/core/markdown/markdown';
import { VisualBlock, VisualPlaceholder } from '@/components/core/visual';
import type { Visual } from '@/types/visual';

/** A concept's visual, placed by the backend as `img://cv-<id>`. */
const CONCEPT_VISUAL = 'img://cv-';
/** Where a visual still being drawn will go (`img://cv-pending-<n>`). */
const PENDING_VISUAL = 'img://cv-pending-';

/**
 * The lesson text's `img` renderer: a concept's visual (`img://cv-<id>`,
 * every label shown), a placeholder where one is still being drawn
 * (`img://cv-pending-<n>`), or a curated / source figure (`img://<id>`) in a
 * lesson written before figures became references only. Any other
 * src renders nothing. Reads the maps at render time, so the component type
 * stays stable across polls.
 */
export function lessonImageRenderer(
  images: MutableRefObject<Record<string, ResolvedLibraryImage>>,
  visuals: MutableRefObject<Record<string, Visual>>
) {
  const LibraryImg = libraryImageRenderer(id => images.current[id]);
  return function Img({ src, alt }: { src?: string; alt?: string }) {
    if (typeof src === 'string' && src.startsWith(PENDING_VISUAL)) {
      return (
        <span className='not-prose my-6 block' data-concept-visual-pending>
          <VisualPlaceholder label={alt} />
        </span>
      );
    }
    if (typeof src === 'string' && src.startsWith(CONCEPT_VISUAL)) {
      const visual = visuals.current[src.slice('img://'.length)];
      if (!visual) return null;
      // `not-prose`: the labelled picture is positioned, and prose's image
      // margins would move it off its labels.
      return (
        <span className='not-prose my-6 block' data-concept-visual>
          <VisualBlock visual={visual} enlargeable />
        </span>
      );
    }
    return <LibraryImg src={src} alt={alt} />;
  };
}
